<script>
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.min.css";

export default {
  name: "modalUploadImage",
  components: {
    Cropper,
  },
  props: {
    type: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      loadingData: false,
      file: null,
      hasImage: false,
      user: this.$A.Auth,
      cropper: {},
      destination:
          "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==",
      image: {},
      src:
          "data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==",
    };
  },
  computed: {
    // The logo and watermark cards each mount this modal on the same page;
    // a shared static id would open (and hide) both at once.
    modalId: function () {
      return "UploadAndCropNewPhoto-" + this.$props.type;
    },
  },
  methods: {
    openModal: function () {
      this.hasImage = false;
      this.file = null;
      this.$bvModal.show(this.modalId);
    },
    // Init on @shown: the modal body isn't in the DOM yet when show() returns,
    // so a setTimeout(0) here raced the render and left cropper uninitialized.
    onModalShown: function () {
      this.image = this.$refs.image;
      this.loadCropper();
    },
    loadCropper: function () {
      if (!this.image) {
        return;
      }
      if (typeof this.cropper.destroy === "function") {
        this.cropper.destroy();
      }
      let options = {};
      if (this.$props.type === "profile") {
        options = {
          zoomable: false,
          scalable: false,
          aspectRatio: 1,
          minCropBoxWidth: 150,
          minCropBoxheight: 150,
          center: true,
        };
      }
      if (this.$props.type === "logo" || this.$props.type === "watermark" || this.$props.type === "emailLogo") {
        options = {
          zoomable: false,
          scalable: false,
          minCropBoxWidth: 150,
          minCropBoxheight: 150,
          center: true,
          cropBoxResizable: true,
        };
      }
      this.cropper = new Cropper(this.image, options);
    },
    loadNewImageSelected: function (e) {
      let self = this;
      // Capture the file from the event itself — the v-model (self.file) can
      // still hold the previously selected file when this handler runs
      const eventFile = e && e.target && e.target.files && e.target.files[0];
      setTimeout(function () {
        let file = eventFile || self.file;

        let reader = new FileReader();
        self.loadingData = true;
        reader.readAsDataURL(file);

        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const elem = document.createElement("canvas");
            elem.width = img.width;
            elem.height = img.height;
            const ctx = elem.getContext("2d");
            ctx.drawImage(img, 0, 0, img.width, img.height);
            const data = ctx.canvas.toDataURL(img, "image/png", 0.8);
            elem.remove();
            // Reveal the crop stage first so the cropper can measure it
            self.hasImage = true;
            self.$nextTick(() => {
              if (typeof self.cropper.replace !== "function") {
                self.image = self.$refs.image;
                self.loadCropper();
              }
              if (typeof self.cropper.replace === "function") {
                self.cropper.replace(data);
              }
              self.loadingData = false;
            });
          };
        };
      }, 100);
    },
    cropImage: function (runAfter) {
      // Never save from an uninitialized cropper — it would silently upload
      // the blank placeholder as the image.
      if (typeof this.cropper.getCroppedCanvas !== "function") {
        this.$A.AlertUser("Something went wrong loading your image. Please close this window and try again.", "warning", 10000);
        return;
      }
      const canvas = this.cropper.getCroppedCanvas();

      canvas.toBlob((blob) => {
        runAfter(blob);
      });
    },
    savePhoto: async function () {
      let self = this;
      this.cropImage(async function (dataBlob) {
        if (self.$props.type === "profile") {
          self.$A.Auth.profileImageUrl = await self.$A.UserService.UploadProfileImage(
              dataBlob
          );
          self.$root.$emit(
              "profile::photo::updated",
              self.$A.Auth.profileImageUrl
          );
        }
        if (self.$props.type === "logo") {
          self.$A.Auth.account.whiteLabelLogoUri = await self.$A.AccountService.UploadLogoPhoto(
              dataBlob
          );
          self.$root.$emit(
              "account::logo::updated",
              self.$A.Auth.account.whiteLabelLogoUri
          );
        }
        if (self.$props.type === "watermark") {
          const watermarkUrl = await self.$A.AccountService.UploadWatermarkImage(
              dataBlob
          );
          self.$root.$emit("account::watermark::updated", watermarkUrl);
        }
        if (self.$props.type === "emailLogo") {
          const emailLogoUrl = await self.$A.AccountService.UploadEmailLogoImage(
              dataBlob
          );
          self.$root.$emit("account::emaillogo::updated", emailLogoUrl);
        }

        self.$A.SaveSessionData(self.$A.Auth);
        self.$bvModal.hide(self.modalId);
      });
    },
  },
  mounted() {
  },
};
</script>

<template>
  <div>
    <b-modal
        :id="modalId"
        size="lg"
        ref="modal"
        no-close-on-backdrop
        content-class="upload-modal-content"
        @shown="onModalShown"
    >
      <template v-slot:modal-header>
        <alertHeader/>
        <div class="w-100 upload-head">
          <div class="upload-head-title">
            {{ $props.type === "profile" ? "Upload Profile Photo" : ($props.type === "watermark" ? "Upload Watermark" : ($props.type === "emailLogo" ? "Upload Email Logo" : "Upload Account Logo")) }}
          </div>
          <div class="upload-head-subtitle">
            Drag in a JPG, PNG, or GIF up to 5MB, then crop it to fit.
          </div>
          <b-spinner
              type="grow"
              label="Spinning"
              class="upload-spinner"
              v-show="loadingData == true"
          ></b-spinner>
        </div>
      </template>

      <form role="form">
        <div class="upload-dropzone" :class="{ 'is-compact': hasImage }">
          <b-form-file
              data-cy="uploadProfileImg"
              v-model="file"
              ref="uploadFileDropper"
              placeholder="Drag an image here or click to browse"
              drop-placeholder="Drop your image here…"
              type="file"
              accept=".jpg, .png, .gif"
              @change="loadNewImageSelected"
          >
          </b-form-file>
        </div>

        <div class="crop-stage" v-show="hasImage">
          <div class="crop-stage-label">Crop your image</div>
          <div class="img-container">
            <img ref="image" :src="src"/>
          </div>
        </div>
      </form>

      <template v-slot:modal-footer="{ cancel }">
        <button type="button" class="secondary-btn" @click="cancel()">Cancel</button>
        <button
            type="button"
            data-cy="saveProfileImg"
            class="primary-btn"
            :disabled="!hasImage"
            @click="savePhoto()"
        >{{ $props.type === "profile" ? "Save As Profile Photo" : ($props.type === "watermark" ? "Save As Watermark" : ($props.type === "emailLogo" ? "Save As Email Logo" : "Save As Account Logo")) }}</button>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
::v-deep .upload-modal-content {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  overflow: hidden;
}
::v-deep .upload-modal-content .modal-header {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
::v-deep .upload-modal-content .modal-body {
  padding: 20px;
}
::v-deep .upload-modal-content .modal-footer {
  display: flex;
  /* a global .modal-footer rule forces flex-direction: column */
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  height: auto;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}
::v-deep .upload-modal-content .modal-footer > * {
  margin: 0;
}

/* Header */
.upload-head {
  position: relative;
}
.upload-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.upload-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.upload-spinner {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #019256;
}

/* Dropzone */
.upload-dropzone ::v-deep .custom-file {
  height: auto;
}
.upload-dropzone ::v-deep .custom-file-input {
  height: 100%;
  cursor: pointer;
}
.upload-dropzone ::v-deep .custom-file-label {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
  height: auto;
  padding: 24px;
  border: 1.5px dashed #c2c8d0;
  border-radius: 10px;
  background: rgba(1, 146, 86, 0.04);
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  text-align: center;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease;
}
.upload-dropzone ::v-deep .custom-file-label:hover {
  border-color: #019256;
  background: rgba(1, 146, 86, 0.08);
}
.upload-dropzone ::v-deep .custom-file-label::after {
  display: none; /* hide the bootstrap "Browse" button */
}
.upload-dropzone.is-compact ::v-deep .custom-file-label {
  min-height: 56px;
  padding: 12px;
}

/* Crop stage */
.crop-stage {
  margin-top: 16px;
}
.crop-stage-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.img-container {
  width: 100%;
  overflow: hidden;
  height: 420px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 10px;
  background: #fbfbfc;
}

/* Buttons */
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 120ms ease, opacity 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
}
.primary-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  background: #fff;
  color: #1f2937;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: border-color 120ms ease, color 120ms ease;
}
.secondary-btn:hover {
  border-color: #019256;
  color: #019256;
}
</style>
